import React, { useEffect, useState } from 'react';
import { Layout } from '@/components/Layout/Layout';
import { MainHeading } from '@/components/common/MainHeading';
import { SectionCard } from '@/components/common/SectionCard';
import { callApi, ThrowError } from '@/api/callApi';
import { API_ENDPOINT, API_URL } from '@/api/constant';
import { Alert, INotification, INotificationListResponse } from '@/interfaces/common.interface';
import { timeAgo, showToastNotification } from '@/utility/helper';
import { AlertTriangle, AlertCircle, Info, BellOff, CheckCheck, Trash2, Plus } from 'lucide-react';
import { useAppDispatch, useAppSelector, RootState } from '@/redux/store';
import { setNotificationCount } from '@/redux/features/UserSlice';
import Pagination from '@/components/common/Pagination';
import { RoutesLink } from '@/utility/RouteLinks';
import { useRoleNavigate } from '@/hooks/useRoleNavigate';
import { RESOURCES } from '@/context/permissions';
import { usePermissions } from '@/hooks/usePermissions';
import ConfirmationModal from '@/components/common/ConfirmationModal';
import { Checkbox } from '@/components/ui/checkbox';
import { CommonSelect } from '@/components/common/CommonSelect';


export default function NotificationsPage() {
    const dispatch = useAppDispatch();
    const { notificationCount } = useAppSelector((state: RootState) => state.user);
    const [notifications, setNotifications] = useState<Alert[]>([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [totalPages, setTotalPages] = useState(1);
    const [totalItems, setTotalItems] = useState(0);
    const navigate = useRoleNavigate();
    const { canCreate } = usePermissions();

    const [markingAllRead, setMarkingAllRead] = useState(false);
    const [showClearAllModal, setShowClearAllModal] = useState(false);
    const [isClearingAll, setIsClearingAll] = useState(false);
    const [notificationToDelete, setNotificationToDelete] = useState<Alert | null>(null);
    const [isDeletingSingle, setIsDeletingSingle] = useState(false);

    // Bulk selection state
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
    const [isDeletingBulk, setIsDeletingBulk] = useState(false);

    const severityIcons = { critical: AlertTriangle, warning: AlertCircle, info: Info };
    const severityColors = { critical: 'text-destructive', warning: 'text-warning', info: 'text-info' };

    const fetchAllNotifications = async (pagenum: number, limitnum: number) => {
        try {
            setLoading(true);
            const response = await callApi<INotificationListResponse>(`${API_URL}${API_ENDPOINT.notification}/user`, {
                method: 'GET',
                data: { page: pagenum, limit: limitnum }
            });

            const rawNotifications = response?.data?.notifications || [];
            if (response?.data?.pageInfo) {
                setTotalPages(response?.data?.pageInfo?.totalPages ?? 1);
                setTotalItems(response?.data?.pageInfo?.total ?? rawNotifications.length);
            }

            const mappedAlerts: Alert[] = rawNotifications.map((n: INotification) => {
                let title = 'Notification';
                if (n.message?.toLowerCase().includes('driver')) title = 'Driver Update';
                else if (n.message?.toLowerCase().includes('order')) title = 'Order Update';
                else if (n?.payload?.type === 'PAYOUT_REQUEST') title = 'Payout Request';

                return {
                    id: n.id,
                    severity: 'info' as const,
                    title: title,
                    message: n.message,
                    read: n.seen,
                    createdAt: n.createdAt,
                    actionUrl: n?.payload?.type === 'PAYOUT_REQUEST'
                        ? RoutesLink.payouts
                        : n.payload?.orderId
                            ? RoutesLink.ViewOrders.replace(':id', n.payload?.orderId)
                            : n.payload?.returnRequestId
                                ? RoutesLink.ViewReturn.replace(":id", n.payload?.returnRequestId)
                                : undefined
                };
            });
            console.log("mappedAlerts", mappedAlerts);


            setNotifications(mappedAlerts);
            setSelectedIds([]);
        } catch (error) {
            console.error('Failed to fetch notifications', error);
        } finally {
            setLoading(false);
        }
    };

    const handleMarkRead = async (id: string, isAlreadyRead: boolean) => {
        if (isAlreadyRead) return;
        try {
            await callApi(`${API_URL}${API_ENDPOINT.notification}/user`, {
                method: 'PATCH',
                data: {
                    ids: [id],
                    all: false,
                    action: 'seen'
                }
            });
            setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
            if (notificationCount > 0) {
                dispatch(setNotificationCount(notificationCount - 1));
            }
        } catch (error) {
            console.error('Failed to mark notification as read', error);
        }
    };

    const handleMarkAllRead = async () => {
        try {
            setMarkingAllRead(true);
            await callApi(`${API_URL}${API_ENDPOINT.notification}/user`, {
                method: 'PATCH',
                data: {
                    ids: [],
                    all: true,
                    action: 'seen'
                }
            });
            setNotifications(prev => prev.map(n => ({ ...n, read: true })));
            dispatch(setNotificationCount(0));
        } catch (error) {
            ThrowError(error);
        } finally {
            setMarkingAllRead(false);
        }
    };

    const handleDeleteSingleNotification = async () => {
        if (!notificationToDelete) return;
        try {
            setIsDeletingSingle(true);
            await callApi(`${API_URL}${API_ENDPOINT.removeNotification}`, {
                method: 'DELETE',
                data: {
                    notificationIds: [notificationToDelete.id]
                }
            });
            showToastNotification('success', 'Notification removed successfully');
            if (!notificationToDelete.read && notificationCount > 0) {
                dispatch(setNotificationCount(notificationCount - 1));
            }
            setNotifications(prev => prev.filter(n => n.id !== notificationToDelete.id));
            setSelectedIds(prev => prev.filter(id => id !== notificationToDelete.id));
            setNotificationToDelete(null);
        } catch (error) {
            ThrowError(error);
        } finally {
            setIsDeletingSingle(false);
        }
    };

    const handleDeleteSelectedNotifications = async () => {
        if (selectedIds.length === 0) return;
        try {
            setIsDeletingBulk(true);
            await callApi(`${API_URL}${API_ENDPOINT.removeNotification}`, {
                method: 'DELETE',
                data: {
                    notificationIds: selectedIds
                }
            });
            showToastNotification('success', `${selectedIds.length} notification(s) removed successfully`);

            const unreadRemovedCount = notifications.filter(
                n => selectedIds.includes(n.id) && !n.read
            ).length;

            if (unreadRemovedCount > 0 && notificationCount > 0) {
                dispatch(setNotificationCount(Math.max(0, notificationCount - unreadRemovedCount)));
            }

            setNotifications(prev => prev.filter(n => !selectedIds.includes(n.id)));
            setSelectedIds([]);
            setShowBulkDeleteModal(false);
        } catch (error) {
            ThrowError(error);
        } finally {
            setIsDeletingBulk(false);
        }
    };

    const handleClearAllNotifications = async () => {
        try {
            setIsClearingAll(true);
            await callApi(`${API_URL}${API_ENDPOINT.emptyNotification}`, {
                method: 'DELETE'
            });
            showToastNotification('success', 'All notifications removed successfully');
            setNotifications([]);
            setSelectedIds([]);
            dispatch(setNotificationCount(0));
            setShowClearAllModal(false);
        } catch (error) {
            ThrowError(error);
        } finally {
            setIsClearingAll(false);
        }
    };

    const isAllSelected = notifications.length > 0 && notifications.every(n => selectedIds.includes(n.id));

    const handleToggleSelectAll = () => {
        if (isAllSelected) {
            setSelectedIds([]);
        } else {
            setSelectedIds(notifications.map(n => n.id));
        }
    };

    useEffect(() => {
        fetchAllNotifications(page, limit);
    }, [page, limit]);


    return (
        <Layout>
            <div className="">
                <MainHeading title="Notifications center">
                    <div className="flex items-center gap-3">
                        {canCreate(RESOURCES.NOTIFICATION) && (
                            <button
                                onClick={() => navigate(RoutesLink.CreateNotification)}
                                className="flex items-center gap-2 h-9 px-4 rounded-xl bg-primary text-white text-sm font-semibold hover:bg-primary/90 transition-all shadow-sm cursor-pointer"
                            >
                                <Plus className="w-4 h-4" />
                                Send Notification
                            </button>
                        )}
                        {notifications.some(n => !n.read) && (
                            <button
                                onClick={handleMarkAllRead}
                                disabled={markingAllRead}
                                className="flex items-center gap-2 h-9 px-4 rounded-xl bg-primary/10 text-primary text-sm font-semibold hover:bg-primary/20 transition-all border border-primary/20 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                            >
                                {markingAllRead ? (
                                    <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                                ) : (
                                    <CheckCheck className="w-4 h-4" />
                                )}
                                {markingAllRead ? 'Marking...' : 'Mark all as read'}
                            </button>
                        )}
                        {selectedIds.length > 0 ? (
                            <button
                                onClick={() => setShowBulkDeleteModal(true)}
                                disabled={isDeletingBulk}
                                className="flex items-center gap-2 h-9 px-4 rounded-xl bg-destructive text-destructive-foreground text-sm font-semibold hover:bg-destructive/90 transition-all shadow-sm cursor-pointer"
                            >
                                <Trash2 className="w-4 h-4" />
                                Remove selected ({selectedIds.length})
                            </button>
                        ) : (
                            notifications.length > 0 && (
                                <button
                                    onClick={() => setShowClearAllModal(true)}
                                    disabled={isClearingAll}
                                    className="flex items-center gap-2 h-9 px-4 rounded-xl bg-destructive/10 text-destructive text-sm font-semibold hover:bg-destructive/20 transition-all border border-destructive/20 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                                >
                                    <Trash2 className="w-4 h-4" />
                                    Remove all
                                </button>
                            )
                        )}
                    </div>
                </MainHeading>
                <SectionCard title="All Notifications" className="min-h-[600px] flex flex-col">
                    <div className="flex-1">
                        {loading ? (
                            <div className="flex flex-col items-center justify-center h-[400px] space-y-4">
                                <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
                                <p className="text-sm text-muted-foreground animate-pulse">Fetching your alerts...</p>
                            </div>
                        ) : notifications.length === 0 ? (
                            <div className="flex flex-col items-center justify-center h-[400px] text-center space-y-4">
                                <div className="w-16 h-16 bg-secondary rounded-full flex items-center justify-center">
                                    <BellOff className="w-8 h-8 text-muted-foreground" />
                                </div>
                                <div>
                                    <h3 className="text-lg font-bold">No notifications found</h3>
                                    <p className="text-sm text-muted-foreground">We'll let you know when something important happens.</p>
                                </div>
                            </div>
                        ) : (
                            <div>
                                <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 px-5 py-3 border-b border-border bg-secondary/20">
                                    <div className="flex items-center gap-3">
                                        <Checkbox
                                            checked={isAllSelected}
                                            onCheckedChange={handleToggleSelectAll}
                                            id="select-all-notifications"
                                            className='cursor-pointer'
                                        />
                                        <label htmlFor="select-all-notifications" className="text-xs font-medium text-muted-foreground cursor-pointer select-none">
                                            Select All ({notifications.length})
                                        </label>
                                        {selectedIds.length > 0 && (
                                            <span className="text-xs font-semibold text-primary">
                                                ({selectedIds.length} selected)
                                            </span>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <span className="text-xs font-semibold text-muted-foreground">Limit:</span>
                                        <CommonSelect
                                            value={String(limit)}
                                            onValueChange={(val) => {
                                                setLimit(Number(val));
                                                setPage(1);
                                            }}
                                            options={[
                                                { label: '10 / page', value: '10' },
                                                { label: '25 / page', value: '25' },
                                                { label: '50 / page', value: '50' },
                                                { label: '100 / page', value: '100' },
                                            ]}
                                            triggerClassName="w-[120px] h-8 text-xs rounded-lg"
                                        />
                                    </div>
                                </div>
                                <div className="divide-y divide-border">
                                    {notifications.map((alert) => {
                                        const Icon = severityIcons[alert.severity] || Info;
                                        const isSelected = selectedIds.includes(alert.id);
                                        return (
                                            <div
                                                key={alert.id}
                                                onClick={() => {
                                                    if (alert.actionUrl) {
                                                        navigate(alert.actionUrl);
                                                    }
                                                    handleMarkRead(alert.id, alert.read)
                                                }}
                                                className={`group relative flex gap-4 p-5 transition-all hover:bg-secondary/30 cursor-pointer ${isSelected ? 'bg-primary/[0.04]' : !alert.read ? 'bg-primary/[0.02]' : ''}`}
                                            >
                                                <div
                                                    onClick={(e) => e.stopPropagation()}
                                                    className="flex items-center pt-2 shrink-0 "
                                                >
                                                    <Checkbox
                                                        checked={isSelected}
                                                        className='cursor-pointer'
                                                        onCheckedChange={() => {
                                                            setSelectedIds(prev =>
                                                                prev.includes(alert.id)
                                                                    ? prev.filter(id => id !== alert.id)
                                                                    : [...prev, alert.id]
                                                            );
                                                        }}
                                                    />
                                                </div>

                                                <div className={`mt-1 w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${!alert.read ? 'bg-primary/10 text-primary' : 'bg-secondary text-muted-foreground'}`}>
                                                    <Icon className="w-5 h-5" />
                                                </div>

                                                <div className="flex-1 space-y-1">
                                                    <div className="flex items-center justify-between gap-2">
                                                        <h4 className={`text-base ${!alert.read ? 'font-bold' : 'font-semibold'} text-foreground`}>
                                                            {alert.title}
                                                        </h4>
                                                        <span className="text-xs text-muted-foreground font-medium">
                                                            {timeAgo(alert.createdAt.toString())}
                                                        </span>
                                                    </div>
                                                    <p className="text-sm text-muted-foreground leading-relaxed max-w-3xl">
                                                        {alert.message}
                                                    </p>
                                                </div>

                                                <div className="flex items-center gap-2 shrink-0">
                                                    {!alert.read && (
                                                        <span className="w-2.5 h-2.5 rounded-full bg-primary shadow-[0_0_10px_rgba(var(--primary),0.5)]" />
                                                    )}
                                                    <button
                                                        type="button"
                                                        title="Remove notification"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setNotificationToDelete(alert);
                                                        }}
                                                        className="p-2 rounded-lg text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors opacity-70 sm:opacity-0 sm:group-hover:opacity-100 cursor-pointer"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>

                    {(totalPages > 1 || notifications.length > 0) && (
                        <div className="mt-8 pt-8 border-t">
                            <Pagination
                                currentPage={page}
                                totalPages={totalPages}
                                totalItems={totalItems}
                                itemsPerPage={limit}
                                onPageChange={setPage}
                                onPageSizeChange={(newSize) => {
                                    setLimit(newSize);
                                    setPage(1);
                                }}
                            />
                        </div>
                    )}
                </SectionCard>
            </div>

            <ConfirmationModal
                open={showClearAllModal}
                onOpenChange={setShowClearAllModal}
                title="Remove All Notifications"
                message="Are you sure you want to remove all notifications? This action cannot be undone."
                confirmText="Remove All"
                type="danger"
                onConfirm={handleClearAllNotifications}
                loading={isClearingAll}
            />

            <ConfirmationModal
                open={showBulkDeleteModal}
                onOpenChange={setShowBulkDeleteModal}
                title="Remove Selected Notifications"
                message={`Are you sure you want to remove the ${selectedIds.length} selected notification(s)? This action cannot be undone.`}
                confirmText={`Remove (${selectedIds.length})`}
                type="danger"
                onConfirm={handleDeleteSelectedNotifications}
                loading={isDeletingBulk}
            />

            <ConfirmationModal
                open={!!notificationToDelete}
                onOpenChange={(open) => !open && setNotificationToDelete(null)}
                title="Remove Notification"
                message="Are you sure you want to remove this notification?"
                confirmText="Remove"
                type="danger"
                onConfirm={handleDeleteSingleNotification}
                loading={isDeletingSingle}
            />
        </Layout>
    );
}

